/**
 * EditRequests — "Website edits" screen.
 *
 * Clients send edits to their site / business from here: content suggestions,
 * working-schedule changes, image uploads, or a general note. The contract
 * allows 2 change requests per calendar month, so the quota is enforced
 * server-side and surfaced here (used / remaining / reset date); the form
 * disables when the allowance is spent.
 *
 * Images are resized in-browser before upload (max 1600px, JPEG) so the payload
 * stays small and well under the API body limit.
 *
 * Data: GET /api/console/edit-requests → { requests, quota }
 *       POST /api/console/edit-requests → { request, quota }
 */
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import { PencilRuler, ImagePlus, X, Check, AlertCircle, Clock } from 'lucide-react';

const CATEGORIES = [
  { value: 'content',  label: 'Website content' },
  { value: 'schedule', label: 'Working hours' },
  { value: 'images',   label: 'Photos / images' },
  { value: 'general',  label: 'Something else' },
];
const STATUS_LABEL = { submitted: 'Received', in_progress: 'In progress', done: 'Done', declined: 'Not actioned' };
// A declined request is given back — it doesn't count against the monthly
// allowance (see editQuota in the dashboard API), so it must not read as a
// failure the client paid for.
const STATUS_COLOR = {
  done:     'var(--good, #20713a)',
  declined: 'var(--ink-soft)',
};
const MAX_IMAGES = 6;

// Resize an image File to a JPEG data URL, max 1600px on the long edge.
function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = 1600;
      let { width, height } = img;
      if (width > max || height > max) {
        const s = max / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad image')); };
    img.src = url;
  });
}

function fmtDate(iso) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' });
}

export default function EditRequests() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['edit-requests'],
    queryFn: () => apiFetch('/api/console/edit-requests'),
    // The operator works these from the dashboard — status moves to
    // in_progress/done/declined and notes get attached AFTER the client's page
    // is already open. Without polling those updates only appeared on a manual
    // refresh, so a client watching the page saw it stuck at "Received" and
    // never saw the operator's note. Poll every 20s (foreground only — the
    // default) and the global refetchOnWindowFocus covers tab-return instantly.
    refetchInterval: 20_000,
    staleTime: 10_000,
  });

  const [category, setCategory] = useState('content');
  const [message, setMessage]   = useState('');
  const [images, setImages]     = useState([]); // [{ data, filename }]
  const [formError, setFormError] = useState('');
  const fileRef = useRef(null);

  const submit = useMutation({
    mutationFn: (payload) => apiFetch('/api/console/edit-requests', { method: 'POST', body: payload }),
    onSuccess: () => {
      setMessage(''); setImages([]); setFormError('');
      queryClient.invalidateQueries({ queryKey: ['edit-requests'] });
    },
    onError: (e) => setFormError(e?.data?.error || e?.message || 'Could not send — try again.'),
  });

  async function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    setFormError('');
    const room = MAX_IMAGES - images.length;
    const next = [];
    for (const f of files.slice(0, room)) {
      try { next.push({ data: await resizeImage(f), filename: f.name }); }
      catch { /* skip unreadable file */ }
    }
    setImages((prev) => [...prev, ...next]);
    if (fileRef.current) fileRef.current.value = '';
  }

  if (isLoading) {
    return (
      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Skeleton style={{ height: 30, width: '40%' }} />
        <Skeleton style={{ height: 160 }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '20px 16px', paddingTop: 64, textAlign: 'center' }}>
        <AlertCircle size={24} color="var(--ink-soft)" style={{ marginBottom: 12 }} />
        <p className="d-xl" style={{ fontSize: 20, color: 'var(--ink)', margin: '0 0 12px' }}>Couldn't load your edits</p>
        <button className="btnx primary" onClick={() => refetch()}>Try again</button>
      </div>
    );
  }

  const quota = data?.quota || { limit: 2, used: 0, remaining: 2, resets_on: '' };
  const requests = data?.requests || [];
  const exhausted = quota.remaining <= 0;
  const canSubmit = !submit.isPending && !exhausted && (message.trim() || images.length);

  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 className="d-xl" style={{ fontSize: 30, color: 'var(--ink)', margin: '0 0 4px' }}>Website edits</h1>
        <p className="serif-i" style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.55 }}>
          Send changes to your site or business — content, hours, or new photos.
        </p>
      </div>

      {/* Quota band */}
      <div className="panel" style={{ padding: 14, position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="brk brk-tl" /><span className="brk brk-br" />
        <Clock size={15} color="var(--acc-ink)" aria-hidden="true" />
        <span style={{ fontSize: 13.5, color: 'var(--ink)' }}>
          <strong>{quota.remaining}</strong> of {quota.limit} edit requests left this month
          {quota.resets_on ? <span style={{ color: 'var(--ink-soft)' }}> · resets {fmtDate(quota.resets_on)}</span> : null}
        </span>
      </div>

      {/* Compose form */}
      <section className="panel" style={{ padding: 20, position: 'relative', opacity: exhausted ? 0.6 : 1 }}>
        <span className="brk brk-tl" /><span className="brk brk-br" />
        <h2 className="mlabel" style={{ margin: '0 0 14px' }}>New edit request</h2>

        <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-soft)', marginBottom: 6 }}>What are you changing?</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={exhausted}
          style={{
            width: '100%', marginBottom: 12,
            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
            background: 'var(--panel-2)', color: 'var(--ink)',
            border: '1px solid var(--line-strong)', borderRadius: 8,
            padding: '10px 34px 10px 12px', fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.4,
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A79F8E' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
            cursor: exhausted ? 'not-allowed' : 'pointer',
          }}>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>{c.label}</option>
          ))}
        </select>

        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} disabled={exhausted}
          placeholder={category === 'schedule'
            ? 'e.g. Open Saturdays 8am–2pm now; closed Mondays.'
            : 'Describe the change you want — be as specific as you like.'}
          style={{ width: '100%', marginBottom: 12, resize: 'vertical' }} />

        {/* Images */}
        {images.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {images.map((im, i) => (
              <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--line)' }}>
                <img src={im.data} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" aria-label="Remove image"
                  onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff', lineHeight: 0, padding: 3 }}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} disabled={exhausted || images.length >= MAX_IMAGES} style={{ display: 'none' }} id="edit-images" />
          <label htmlFor="edit-images" className="btnx quiet" style={{ cursor: exhausted || images.length >= MAX_IMAGES ? 'not-allowed' : 'pointer', opacity: images.length >= MAX_IMAGES ? 0.5 : 1 }}>
            <ImagePlus size={15} aria-hidden="true" /> Add photos {images.length ? `(${images.length}/${MAX_IMAGES})` : ''}
          </label>
          <button type="button" className="btnx primary" disabled={!canSubmit}
            onClick={() => submit.mutate({ category, message: message.trim(), images })}
            style={{ opacity: canSubmit ? 1 : 0.5 }}>
            {submit.isPending ? 'Sending…' : 'Send edit request'}
          </button>
        </div>

        {exhausted && (
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--ink-soft)' }}>
            You've used both edits for this month. Your allowance resets {quota.resets_on ? fmtDate(quota.resets_on) : 'next month'}.
          </p>
        )}
        {formError && <p role="alert" style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--bad)' }}>{formError}</p>}
      </section>

      {/* History */}
      <section className="panel" style={{ padding: 20, position: 'relative' }}>
        <span className="brk brk-tl" /><span className="brk brk-br" />
        <h2 className="mlabel" style={{ margin: '0 0 14px' }}>Your requests</h2>
        {requests.length === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>No edit requests yet — your first one will show here.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requests.map((r) => (
              <div key={r.id} className="lrow" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 3px', fontSize: 14, color: 'var(--ink)' }}>
                    {CATEGORIES.find((c) => c.value === r.category)?.label || r.category}
                    {r.image_count > 0 ? <span className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)' }}> · {r.image_count} photo{r.image_count === 1 ? '' : 's'}</span> : null}
                  </p>
                  {r.message && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.message}</p>}
                  <p className="mono" style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--ink-soft)' }}>{fmtDate(r.created_at)}</p>
                  {r.operator_note && (
                    <div style={{
                      margin: '7px 0 0', padding: '8px 10px',
                      // --panel-2 is the theme-aware sunken surface (dark in dark
                      // mode). The old var(--sunk, #F7F5F1) had no such variable
                      // defined, so it always fell back to hardcoded white —
                      // invisible against --ink text in dark mode.
                      background: 'var(--panel-2)',
                      borderLeft: '2px solid var(--acc, #E8A020)',
                    }}>
                      <span className="mono" style={{ display: 'block', fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--acc-ink)', marginBottom: 3 }}>
                        Note from Titan
                      </span>
                      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>
                        {r.operator_note}
                      </p>
                    </div>
                  )}
                </div>
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: STATUS_COLOR[r.status] || 'var(--acc-ink)', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  {r.status === 'done' && <Check size={11} />}{STATUS_LABEL[r.status] || r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
